import React, {useMemo, useState} from "react";
import {Dialog, DialogBackdrop, DialogPanel, DialogTitle} from "@headlessui/react";
import {Link} from "react-router-dom";
import {faXmark} from "@fortawesome/pro-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import UseProjectsContext from "../../services/context/projects/use-projects-context.ts";
import SimpleLoadingIndicator from "../simple-loading-indicator";
import SearchBar from "../searchbar";
import useFilteredData from "../../utils/filter-data.ts";
import BaseDialog from "../../models/interfaces/base-dialog.ts";
import Button from "../buttons/button";

interface ProjectSelectorProps extends BaseDialog {
    showCloseButton?: boolean;
}

const ProjectSelector: React.FC<ProjectSelectorProps> = ({dialogOpen, setDialogOpen, showCloseButton = false}) => {
    const { isLoading, projects } = UseProjectsContext();
    const [searchQuery, setSearchQuery] = useState('');

    const filteredData = useFilteredData({
        data: projects,
        searchQuery,
        searchFields: ['name']
    });

    const sortedData = useMemo(() => {
        if(!filteredData) return [];
        return filteredData.sort((a, b) => a.name.localeCompare(b.name));
    }, [filteredData]);
    
    if(isLoading)
        return <SimpleLoadingIndicator text='Chargement des projets...'/>;
    
    if(projects == null)
        return <div>Erreur de chargement des projets</div>
    
    return (
        <Dialog open={dialogOpen} onClose={setDialogOpen} className='relative z-5001'>
            <DialogBackdrop
                transition
                className='fixed inset-0 bg-gray-500/75 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in'
            />

            <div className='fixed inset-0 z-5001 w-screen overflow-y-auto'>
                <div className='flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0'>
                    <DialogPanel
                        transition
                        className='flex flex-col gap-y-2 relative overflow-hidden max-h-[80vh] min-w-[100vh] rounded-lg bg-white text-left shadow-xl transition-all data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-closed:sm:translate-y-0 data-closed:sm:scale-95'
                    >
          
                        <DialogTitle as='h3' className='flex flex-row text-base text-gray-900 items-center'>
                            <div className='flex flex-row items-center border-b border-gray-200 w-full pb-4'>
                                <div className=' text-xl'>Sélectionner un projet</div>

                                {showCloseButton && (
                                    <Button onClick={() => setDialogOpen(false)} type='button' variant='ghost' className='ml-auto'>
                                        <FontAwesomeIcon icon={faXmark} className='text-red-500 text-xl'/>
                                    </Button>
                                )}
                            </div>
                        </DialogTitle>
                        
                        <div className='flex flex-col gap-y-4 overflow-hidden'>
                            <SearchBar onSearch={setSearchQuery} className={'ml-auto w-96'}/>

                            <ul className='divide-y divide-gray-200 overflow-y-auto'>
                                {sortedData.map(project => (
                                    <li className='hover:bg-blue-200 cursor-pointer' key={project.id}>
                                        <Link to={`/takeoff/${project.id}`} className='block w-full h-full px-4 py-2' onClick={() => setDialogOpen(false)} > 
                                            {project.name}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    )
}

export default ProjectSelector;